import type { SxProps, Theme } from '@mui/material/styles';

/** The column count `distributeSpans` (cardLayout.ts) hands out spans out of.
 *  Every other breakpoint below is a fraction of this one. */
export const DESKTOP_GRID_COLUMNS = 8;

/**
 * Column count by viewport width, widest first, matching the density spec:
 * 12 at 1440 and up, 8 from 1200, 4 from 900, 1 below that. A plain
 * mobile-first `min-width` cascade, so each breakpoint only overrides what
 * changes.
 */
export const LENS_GRID_BREAKPOINTS = [
  { minWidth: 0, columns: 1 },
  { minWidth: 900, columns: 4 },
  { minWidth: 1200, columns: 8 },
  { minWidth: 1440, columns: 12 },
] as const;

/**
 * Scales a span given out of the desktop column count to another column
 * count. A single proportional formula applied to every span guarantees a
 * wider desktop card is never mapped to a narrower tablet share than a
 * smaller one: two spans that satisfy a < b keep spanA/columns <=
 * spanB/columns after rounding, so their order survives the rounding.
 * Clamped to the row itself so a span can never claim more columns than it
 * has, and floored at one so a card never disappears to zero width.
 */
export function responsiveSpan(span: number, columns: number, desktopColumns: number = DESKTOP_GRID_COLUMNS): number {
  if (columns <= 0) {
    return 0;
  }
  if (desktopColumns <= 0) {
    return Math.min(columns, 1);
  }
  const scaled = Math.round((span / desktopColumns) * columns);
  return Math.min(columns, Math.max(1, scaled));
}

/**
 * Keep the 24px grid rhythm while reducing the visible space between cards.
 * The taller row track gives existing frames essentially the same usable
 * height, while the 8px gutter makes the canvas read as one clean dashboard.
 */
export const LENS_FRAME_ROW_PX = 16;
export const LENS_FRAME_GAP_PX = 8;

/**
 * Frame rows a drag, a drop or a resize moves in.
 *
 * A row is 16px so a card's declared height can be described exactly, which
 * makes it a poor thing to aim at: with the gap, dropping snapped every 24px
 * vertically against twelve fixed positions horizontally, so cards meant to sit
 * level landed a row or two apart with nothing to pull them together. Moving in
 * fours puts a vertical target every 100px without changing what a frame can
 * describe, so saved boards keep their exact layout until a card is moved.
 */
export const LENS_FRAME_ROW_STEP = 4;

/** The nearest step boundary, never below the first row. */
export function snapFrameRows(rows: number): number {
  return Math.max(LENS_FRAME_ROW_STEP, Math.round(rows / LENS_FRAME_ROW_STEP) * LENS_FRAME_ROW_STEP);
}

export function frameRowSpan(cardHeightPx: number): number {
  return Math.max(1, Math.ceil((cardHeightPx + LENS_FRAME_GAP_PX) / (LENS_FRAME_ROW_PX + LENS_FRAME_GAP_PX)));
}

/**
 * One card in the saved Lens frame. Cards are full-width on a phone, then
 * snap to proportional columns and a declared row height from tablet upward.
 * `grid-auto-flow: dense` on the parent can reuse an earlier vacant frame;
 * it never changes persisted card order or lets cards overlap.
 */
export function cardResponsiveSx(span: number, cardHeightPx: number): SxProps<Theme> {
  const [mobile, ...rest] = LENS_GRID_BREAKPOINTS;
  const rowSpan = frameRowSpan(cardHeightPx);
  const sx: Record<string, unknown> = {
    gridColumn: `span ${responsiveSpan(span, mobile.columns)}`,
    gridRow: 'auto',
    minWidth: 0,
  };
  for (const breakpoint of rest) {
    sx[`@media (min-width:${breakpoint.minWidth}px)`] = {
      gridColumn: `span ${responsiveSpan(span, breakpoint.columns)}`,
      gridRow: `span ${rowSpan}`,
    };
  }
  return sx as SxProps<Theme>;
}

/**
 * Class the grid engine looks for to start a drag.
 *
 * Only the grip moves a card. Without a handle, a click meant for a chart mark,
 * a card menu or a cross-filter would be read as the beginning of a drag.
 */
export const LENS_DRAG_HANDLE_CLASS = 'lens-card-drag-handle';

/** The slot a swapping card is leaving, and the one it is going to. */
export const LENS_SWAP_FROM_CLASS = 'lens-swap-from';
export const LENS_SWAP_TO_CLASS = 'lens-swap-to';
